import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CircleNotch, FilePdf, CheckCircle, XCircle, WarningCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const TITLES = {
  general: {
    title: "Year-End CPA Bundle",
    description: "One ZIP for your CPA with Schedule C, annual statements, receipts, mileage, and transaction exports.",
  },
  contractor: {
    title: "Contractor Year-End Bundle",
    description: "Includes the base tax packet plus job costing, equipment depreciation, 1099 totals, and lien-waiver tracking when recorded.",
  },
  real_estate: {
    title: "Rental Property Year-End Bundle",
    description: "For rental and investment property businesses. Includes the base tax packet plus property-level P&L, a Schedule E-style summary, mortgage-interest and depreciation schedules when recorded.",
  },
  brokerage: {
    title: "Real Estate Brokerage Year-End Bundle",
    description: "For agents and brokers. Includes the base tax packet plus a commission income summary and a marketing & listing expense summary. Rental-property reports (Schedule E, property P&L) are not included for brokerage activity.",
  },
  wellness: {
    title: "Wellness Year-End Bundle",
    description: "Includes the base tax packet plus service revenue, commission splits, and membership revenue reporting when tracked.",
  },
  trucking: {
    title: "Trucking Year-End Bundle",
    description: "Includes the base tax packet plus IFTA-style fuel summaries, per-diem logging, and load profitability when the data is in your books.",
  },
  restaurant: {
    title: "Restaurant Year-End Bundle",
    description: "Includes the base tax packet plus food cost, tip payroll, and daily sales reconciliation exports when recorded.",
  },
};

const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

export function YearEndBundleSection({ businessId, business }) {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [bundlePreviewLoading, setBundlePreviewLoading] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [bundlePreview, setBundlePreview] = useState(null);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [showGapHints, setShowGapHints] = useState(false);

  const verticalMeta = useMemo(() => TITLES[bundlePreview?.vertical || "general"] || TITLES.general, [bundlePreview]);

  const fetchBundlePreview = async ({ showError = false } = {}) => {
    setBundlePreviewLoading(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/taxes/year-end-bundle-preview?year=${year}&method=cash`);
      setBundlePreview(data);
      return data;
    } catch (err) {
      if (showError) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't build the bundle preview.");
      return null;
    } finally {
      setBundlePreviewLoading(false);
    }
  };

  const loadHistory = () => api.get(`/businesses/${businessId}/taxes/year-end-bundle-history`).then((r) => setHistory(r.data || [])).catch(() => {});

  useEffect(() => {
    fetchBundlePreview();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, year]);

  const downloadYearEndBundle = async (targetYear = year, regenerate = false) => {
    setBundleLoading(true);
    try {
      const res = await api.get(`/businesses/${businessId}/taxes/year-end-bundle.zip?year=${targetYear}&method=cash`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ironroot-year-end-tax-bundle-${targetYear}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      loadHistory();
      if (regenerate) {
        toast.success(`Regenerated your ${targetYear} bundle with current data.`);
      } else {
        toast.success(`Downloaded your ${targetYear} Year-End Bundle`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't build the year-end bundle.");
    } finally {
      setBundleLoading(false);
    }
  };

  const openBundlePreview = async () => {
    const payload = await fetchBundlePreview({ showError: true });
    if (!payload) return;
    setShowGapHints(false);
    setBundleModalOpen(true);
  };

  const jumpToGap = (tabTestId) => {
    const tab = document.querySelector(`[data-testid="${tabTestId}"]`);
    if (tab) {
      tab.click();
      tab.scrollIntoView({ behavior: "smooth", block: "center" });
      return true;
    }
    return false;
  };

  const pulseTarget = (selector) => {
    setTimeout(() => {
      const el = document.querySelector(selector);
      if (!el) return;
      const prevOutline = el.style.outline;
      const prevBoxShadow = el.style.boxShadow;
      el.style.outline = "2px solid rgba(240, 180, 41, 0.9)";
      el.style.boxShadow = "0 0 0 0 rgba(240, 180, 41, 0.45)";
      el.animate([
        { boxShadow: "0 0 0 0 rgba(240, 180, 41, 0.45)", transform: "scale(1)" },
        { boxShadow: "0 0 0 14px rgba(240, 180, 41, 0.08)", transform: "scale(1.01)" },
        { boxShadow: "0 0 0 0 rgba(240, 180, 41, 0)", transform: "scale(1)" },
      ], { duration: 1500, iterations: 2 });
      setTimeout(() => {
        el.style.outline = prevOutline;
        el.style.boxShadow = prevBoxShadow;
      }, 3200);
    }, 420);
  };

  const goFixThisNow = (item, closeModal = false) => {
    if (!item) return;
    if (closeModal) setBundleModalOpen(false);
    setShowGapHints(true);
    const path = window.location.pathname;
    const config = {
      receipts_unattached: { url: `${path}?tab=receipts&receipt_gap=unattached`, panel: '[data-testid="receipt-unattached-card"]' },
      mileage: { url: `${path}?tab=mileage`, panel: '[data-testid="mileage-tab"]' },
      jobs: { url: `${path}?tab=jobs`, panel: '[data-testid="jobs-panel"]' },
      memberships: { url: `${path}?tab=memberships`, panel: '[data-testid="memberships-panel"]' },
      supplies: { url: `${path}?tab=supplies`, panel: '[data-testid="supplies-panel"]' },
      books: { url: `${path}?tab=books`, panel: '[data-testid="books-ledger"]' },
      books_uncategorized: { url: `${path}?tab=books&category=Other`, panel: '[data-testid="books-category-filter-pill"]' },
      taxes: { url: `${path}?tab=taxes`, panel: '[data-testid="taxes-panel"]' },
      accounting_reports: { url: `${path}?tab=accounting`, panel: '[data-testid="reports-controls-card"]', inner: '[data-testid="accounting-tab-reports"]' },
    };
    const target = config[item.target] || { url: `${path}?tab=taxes`, panel: '[data-testid="taxes-panel"]' };
    navigate(target.url);
    if (target.inner) {
      setTimeout(() => document.querySelector(target.inner)?.click(), 220);
    }
    pulseTarget(target.panel);
    toast.info("Opened the area where this gap can be fixed.");
  };

  const fixGapsFirst = () => {
    const firstAction = bundlePreview?.warning_items?.[0];
    if (firstAction) {
      goFixThisNow(firstAction, true);
      return;
    }
    setBundleModalOpen(false);
    setShowGapHints(true);
    const focusTarget = bundlePreview?.warning_meta?.focus_target;
    const mapping = {
      mileage: "tab-mileage",
      jobs: "tab-jobs",
      memberships: "tab-memberships",
      supplies: "tab-supplies",
      books: "tab-books",
      taxes: "tab-taxes",
    };
    if (focusTarget && mapping[focusTarget] && jumpToGap(mapping[focusTarget])) return;
    const warningCard = document.querySelector('[data-testid="year-end-bundle-warnings"]');
    warningCard?.scrollIntoView({ behavior: "smooth", block: "center" });
    toast.info("Bundle gaps are highlighted below.");
  };

  return (
    <div className="bk-card p-6" data-testid="year-end-bundle-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <FilePdf size={18} weight="duotone" color="var(--bk-accent)" />
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{verticalMeta.title}</h3>
          </div>
          <p className="text-sm mt-2 max-w-3xl" style={{ color: "var(--bk-text-2)" }}>{verticalMeta.description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="year-end-bundle-year" className="rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
            {years.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <button onClick={openBundlePreview} disabled={bundlePreviewLoading} data-testid="year-end-download-bundle" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70">
            {bundlePreviewLoading ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} weight="bold" />} Download Year-End Bundle
          </button>
        </div>
      </div>

      {showGapHints && (bundlePreview?.warnings || []).length > 0 && (
        <div className="mt-4 rounded-2xl border p-4" style={{ borderColor: "#F0C36D", background: "#FFF9ED" }} data-testid="year-end-bundle-warnings">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={18} weight="fill" color="#B45309" />
            <p className="text-sm font-semibold" style={{ color: "#8A5A13" }}>Fix these bundle gaps first</p>
          </div>
          <ul className="space-y-2 text-sm" style={{ color: "#7C5A28" }}>
            {(bundlePreview?.warning_items || []).map((item) => (
              <li key={item.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span>{item.text}</span>
                <button onClick={() => goFixThisNow(item)} data-testid={`warning-action-${item.key}`} className="text-xs font-semibold underline underline-offset-4 text-left sm:text-right">
                  Fix this now →
                </button>
              </li>
            ))}
            {!(bundlePreview?.warning_items || []).length && (bundlePreview?.warnings || []).map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </div>
      )}

      <Dialog open={bundleModalOpen} onOpenChange={setBundleModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="year-end-bundle-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Preview bundle contents</DialogTitle>
            <DialogDescription>
              Review what will be included, what is missing, and jump straight to any cleanup area before downloading your CPA bundle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="rounded-2xl p-4" style={{ background: (bundlePreview?.warnings || []).length ? "#FFF9ED" : "var(--bk-rowan-bg)", border: "1px solid rgba(232,168,56,0.24)" }} data-testid="year-end-bundle-rowan-summary">
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-accent)" }}>Rowan says</p>
              <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }}>{bundlePreview?.rowan_summary}</p>
            </div>

            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }}>
              <div className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>
                Included in your bundle
              </div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {(bundlePreview?.documents || []).map((doc) => (
                  <div key={doc.key} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`bundle-preview-doc-${doc.key}`}>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{doc.label}</p>
                      <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{doc.detail}</p>
                    </div>
                    <div className="shrink-0 flex items-center gap-2 text-sm font-semibold" style={{ color: doc.included ? "var(--bk-success)" : "#B23B1F" }}>
                      {doc.included ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
                      <span>{doc.included ? "Included" : "Missing"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {(bundlePreview?.warnings || []).length > 0 && (
              <div className="rounded-2xl border p-4" style={{ borderColor: "#F0C36D", background: "#FFF9ED" }} data-testid="bundle-preview-gaps">
                <div className="flex items-center gap-2 mb-2">
                  <WarningCircle size={18} weight="fill" color="#B45309" />
                  <p className="text-sm font-semibold" style={{ color: "#8A5A13" }}>Gaps & warnings</p>
                </div>
                <ul className="space-y-2 text-sm" style={{ color: "#7C5A28" }}>
                  {(bundlePreview.warning_items || []).map((item) => (
                    <li key={item.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span>{item.text}</span>
                      <button onClick={() => goFixThisNow(item, true)} data-testid={`modal-warning-action-${item.key}`} className="text-xs font-semibold underline underline-offset-4 text-left sm:text-right">
                        Fix this now →
                      </button>
                    </li>
                  ))}
                  {!(bundlePreview.warning_items || []).length && bundlePreview.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                </ul>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button onClick={fixGapsFirst} data-testid="bundle-preview-fix-gaps" className="px-4 py-2.5 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                Fix gaps first
              </button>
              <button onClick={async () => { setBundleModalOpen(false); await downloadYearEndBundle(); }} disabled={bundleLoading} data-testid="bundle-preview-download-anyway" className="bk-btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-70">
                {bundleLoading ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} weight="bold" />} Download as-is
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-5 rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }} data-testid="bundle-history-card">
        <div className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Bundle History</div>
        <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
          {history.length === 0 ? (
            <div className="px-4 py-5 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="bundle-history-empty">No bundle history yet. Your first download will appear here.</div>
          ) : history.map((entry) => (
            <div key={entry.id} className="px-4 py-4 grid grid-cols-1 md:grid-cols-[100px_170px_1fr_auto] gap-3 items-start" data-testid={`bundle-history-row-${entry.id}`}>
              <div>
                <p className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Tax Year</p>
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{entry.tax_year}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Generated</p>
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{new Date(entry.generated_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Bundle Contents</p>
                <p className="text-sm" style={{ color: "var(--bk-text)" }}>{entry.bundle_summary}</p>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <button onClick={() => downloadYearEndBundle(entry.tax_year, false)} data-testid={`bundle-history-download-${entry.id}`} className="px-3 py-2 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Download Again</button>
                <button onClick={() => downloadYearEndBundle(entry.tax_year, true)} data-testid={`bundle-history-regenerate-${entry.id}`} className="bk-btn-primary px-3 py-2 text-xs font-semibold">Regenerate</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}